import { msalInstance } from '../../auth/msalConfig.js';
import type { IconName } from '../../components/ui/Icon.js';

/**
 * Client-side cache for the top-bar "Recent" and "Favorites" quick-access menus.
 *
 * The authoritative copy lives in the database (see QuickAccessProvider); these
 * localStorage entries are only a per-account cache so the menus paint instantly on load
 * before the server copy arrives. Keys are scoped per signed-in account (mirroring the
 * theme provider) so each user keeps their own lists on a shared machine.
 */

/** A record the user viewed/edited (customer, project, ticket, …). */
export interface QuickAccessRecord {
  kind: 'record';
  /** Entity type, e.g. 'customer' | 'project' | 'ticket'. */
  entity: string;
  id: string;
  label: string;
  /** Route that reopens the record. */
  path: string;
  /** Epoch ms of the last visit. */
  at: number;
}

/** A pinned navigation menu item. */
export interface QuickAccessMenu {
  kind: 'menu';
  key: string;
  label: string;
  path: string;
  icon: IconName;
  at: number;
}

/** Recents are always records; favorites may be a record OR a menu item. */
export type RecentItem = QuickAccessRecord;
export type FavoriteItem = QuickAccessRecord | QuickAccessMenu;

/**
 * Input for toggling a favorite (no `at` timestamp yet). Declared as a union of each
 * member's Omit — a plain `Omit<FavoriteItem, 'at'>` would collapse the union to only its
 * shared keys and drop the entity/menu-specific fields.
 */
export type FavoriteInput = Omit<QuickAccessRecord, 'at'> | Omit<QuickAccessMenu, 'at'>;

export const RECENTS_LIMIT = 10;
export const FAVORITES_LIMIT = 100;

const RECENTS_KEY = 'catalyst:recents';
const FAVORITES_KEY = 'catalyst:favorites';

/** Stable identity for de-duping and favorite lookups. */
export function itemKey(item: FavoriteItem): string {
  return item.kind === 'menu' ? `menu:${item.key}` : `record:${item.entity}:${item.id}`;
}

/** Per-account storage suffix so each signed-in account keeps its own cache. */
function accountSuffix(): string | null {
  const account = msalInstance?.getActiveAccount() ?? msalInstance?.getAllAccounts()[0];
  return account ? account.homeAccountId : null;
}

function storageKey(base: string): string {
  const suffix = accountSuffix();
  return suffix ? `${base}:${suffix}` : base;
}

function read<T>(base: string): T[] {
  try {
    const raw = localStorage.getItem(storageKey(base));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function write<T>(base: string, value: T[]): void {
  try {
    localStorage.setItem(storageKey(base), JSON.stringify(value));
  } catch {
    /* localStorage unavailable; the in-memory list still applies for this session. */
  }
}

export function readRecents(): RecentItem[] {
  return read<RecentItem>(RECENTS_KEY);
}

export function readFavorites(): FavoriteItem[] {
  return read<FavoriteItem>(FAVORITES_KEY);
}

export function writeRecents(value: RecentItem[]): void {
  write(RECENTS_KEY, value);
}

export function writeFavorites(value: FavoriteItem[]): void {
  write(FAVORITES_KEY, value);
}

/**
 * Reconciles local edits made before the server copy arrived with the server copy itself.
 * Items are unioned by {@link itemKey}, keeping whichever copy was touched most recently,
 * then sorted newest-first and capped. Only used when the user actually changed something
 * during the load — otherwise the server copy wins outright, so a removal made on another
 * device isn't resurrected by this device's stale cache.
 */
export function mergeItems<T extends FavoriteItem>(local: T[], server: T[], limit: number): T[] {
  const byKey = new Map<string, T>();
  for (const item of [...server, ...local]) {
    const key = itemKey(item);
    const existing = byKey.get(key);
    if (!existing || item.at > existing.at) byKey.set(key, item);
  }
  return [...byKey.values()].sort((a, b) => b.at - a.at).slice(0, limit);
}
